/**
 * Single source of truth for the locale used to format dates, times and numbers
 * for display.
 *
 * We auto-detect from the browser (navigator.language) so a user's machine
 * preference drives formatting - e.g. a UK visitor sees 15/03/2024 and 1,234.5
 * while a US visitor sees 03/15/2024, with no configuration. Every formatter and
 * call site should resolve its locale through getLocale() rather than hardcoding
 * 'en-US', so the day a stored user/org preference is introduced it only has to
 * be wired in here.
 */

const FALLBACK_LOCALE = 'en-US';

/**
 * Returns true when `tag` is a well-formed BCP-47 locale that the Intl APIs
 * accept. Crawlers and some browsers report non-standard navigator.language
 * values (e.g. "C", "POSIX", "en_US") that crash toLocaleDateString/Intl with
 * `RangeError: Incorrect locale information provided`, so we must reject them.
 */
function isValidLocale(tag: string): boolean {
  try {
    Intl.getCanonicalLocales(tag);
    return true;
  } catch {
    return false;
  }
}

/**
 * The BCP-47 locale tag to format display values in. Reads navigator.language
 * (the browser/OS preference) and falls back to en-US when unavailable (SSR,
 * non-browser test runners, or a browser that doesn't expose it) or when the
 * reported value is not a valid locale tag (crawlers, misconfigured clients).
 */
export function getLocale(): string {
  if (typeof navigator !== 'undefined' && navigator.language && isValidLocale(navigator.language)) {
    return navigator.language;
  }
  return FALLBACK_LOCALE;
}
